// AccountContextSwitcher — small top-bar dropdown that lets the pilot
// flip between Personal mode and any organization they're a member of.
//
// Hidden entirely when the user has no orgs (then Personal is the only
// option and the menu would be pointless).

import { useState, useRef, useEffect } from 'react';
import { User, Building2, ChevronDown, Check } from '../shell/Icon';
import {
  useAccountContextStore,
} from '../stores/accountContextStore';
import { useOrgStore } from '../stores/orgStore';

type Variant = 'bar' | 'rail';

export default function AccountContextSwitcher({ variant = 'bar' }: { variant?: Variant } = {}) {
  const ctx = useAccountContextStore((s) => s.ctx);
  const switchToPersonal = useAccountContextStore((s) => s.switchToPersonal);
  const switchToOrganization = useAccountContextStore((s) => s.switchToOrganization);
  const orgs = useOrgStore((s) => s.orgs);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Hide entirely when there's nothing to switch to.
  if (orgs.length === 0) return null;

  const activeOrg = ctx.kind === 'organizational'
    ? orgs.find((o) => o.id === ctx.orgId) ?? null
    : null;

  // Rail variant: icon-only square button (sized for the 64px desktop
  // SideRail), menu pops to the right.
  if (variant === 'rail') {
    return (
      <div className="relative" ref={rootRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          title={ctx.kind === 'personal' ? 'Personal' : (activeOrg?.name ?? 'Organization')}
          aria-label="Switch account context"
          aria-haspopup="menu"
          aria-expanded={open}
          className="h-10 w-10 rounded-lg flex items-center justify-center bg-cockpit-700/70 border border-cockpit-600 hover:bg-cockpit-600/80"
        >
          {ctx.kind === 'personal'
            ? <User size={16} className="text-cockpit-200" />
            : <Building2 size={16} className="text-ember-400" />}
        </button>
        {open && (
          <div
            role="menu"
            className="absolute left-full top-0 ml-2 w-64 z-50 bg-cockpit-800 border border-cockpit-600 rounded-lg shadow-xl py-1 text-sm"
          >
            <MenuItem
              label="Personal"
              sub="Your own aircraft + flights"
              icon={<User size={13} className="text-cockpit-300" />}
              active={ctx.kind === 'personal'}
              onClick={() => { switchToPersonal(); setOpen(false); }}
            />
            {orgs.length > 0 && <div className="border-t border-cockpit-700 my-1" />}
            {orgs.map((o) => (
              <MenuItem
                key={o.id}
                label={o.name}
                sub={o.legal_name ?? 'Shared with org members'}
                icon={<Building2 size={13} className="text-ember-400" />}
                active={ctx.kind === 'organizational' && ctx.orgId === o.id}
                onClick={() => { switchToOrganization(o.id); setOpen(false); }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Bar variant (mobile MapTopBar): full pill button with label.
  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-cockpit-700/70 border border-cockpit-600 text-xs text-cockpit-100 hover:bg-cockpit-600/80"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {ctx.kind === 'personal'
          ? <User size={12} className="text-cockpit-300" />
          : <Building2 size={12} className="text-ember-400" />}
        <span className="max-w-[140px] truncate">
          {ctx.kind === 'personal' ? 'Personal' : (activeOrg?.name ?? 'Organization')}
        </span>
        <ChevronDown size={12} className="text-cockpit-400" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-64 z-50 bg-cockpit-800 border border-cockpit-600 rounded-lg shadow-xl py-1 text-sm"
        >
          <MenuItem
            label="Personal"
            sub="Your own aircraft + flights"
            icon={<User size={13} className="text-cockpit-300" />}
            active={ctx.kind === 'personal'}
            onClick={() => { switchToPersonal(); setOpen(false); }}
          />
          {orgs.length > 0 && (
            <div className="border-t border-cockpit-700 my-1" />
          )}
          {orgs.map((o) => (
            <MenuItem
              key={o.id}
              label={o.name}
              sub={o.legal_name ?? 'Shared with org members'}
              icon={<Building2 size={13} className="text-ember-400" />}
              active={ctx.kind === 'organizational' && ctx.orgId === o.id}
              onClick={() => { switchToOrganization(o.id); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label, sub, icon, active, onClick,
}: {
  label: string;
  sub?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      role="menuitemradio"
      aria-checked={active}
      className={[
        'w-full text-left px-2.5 py-1.5 flex items-center gap-2 hover:bg-cockpit-700',
        active ? 'bg-cockpit-700/50' : '',
      ].join(' ')}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-cockpit-100 truncate">{label}</span>
        {sub && <span className="block text-[10px] text-cockpit-400 truncate">{sub}</span>}
      </span>
      {active && <Check size={12} className="text-emerald-400 flex-shrink-0" />}
    </button>
  );
}
